import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  SHARE_INTERACTION_EMAIL_PREVIEW_QUERY,
  type ShareInteractionEmailPreview,
  type ShareInteractionEmailPreviewQueryResponse,
  type ShareInteractionEmailPreviewQueryVariables
} from "../queries";
import type { ShareInteractionPreviewInput } from "../shareInteractionPreviewInput";

export function useShareInteractionEmailPreview() {
  const [preview, setPreview] = useState<ShareInteractionEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    ShareInteractionEmailPreviewQueryResponse,
    ShareInteractionEmailPreviewQueryVariables
  >(SHARE_INTERACTION_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (input: ShareInteractionPreviewInput) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: { input }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.shareInteractionEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
